import debounce from 'lodash/debounce';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { FaqWrap } from '@/cms/FaqWrap/FaqWrap';
import { Typography } from '@/components/Typography';
import type {
  ContentfulFaqCategoriesFragment,
  ContentfulFaqWrapFragment,
} from '@/graphql/contentful/api';
import { useIsMdDown } from '@/hooks/useMedia';
import { messages } from '@/messages/faq';

import * as S from './FaqCategories.styles';

type faqWrapsCollectionItemType = ContentfulFaqWrapFragment | undefined | null;
export const RenderContentfulFaqCategoriesSection = (
  props: ContentfulFaqCategoriesFragment,
) => {
  const { formatMessage } = useIntl();
  const isMdDown = useIsMdDown();

  const [isOpen, setIsOpen] = useState(false);
  const [isHeaderBelowFaqsTop, setIsHeaderBelowFaqsTop] = useState(false);
  const [isHeaderBottomWithinLinks, setIsHeaderBottomWithinLinks] =
    useState(false);
  const [headerBoxHeight, setHeaderBoxHeight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const desktopWrapperRef = useRef<HTMLDivElement>(null);
  const handleScrollOrResize = useCallback(
    debounce(() => {
      const headerWrapper = document.querySelector(
        '[data-testid="HeaderWrapper"]',
      );
      if (desktopWrapperRef.current && containerRef.current && headerWrapper) {
        const headerBoxHeight = parseFloat(
          window.getComputedStyle(headerWrapper).height,
        );
        setHeaderBoxHeight(headerBoxHeight);
        const headerWrapperRect = headerWrapper.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        const linksWrapperDesktopRect =
          desktopWrapperRef.current.getBoundingClientRect();

        if (headerWrapperRect.bottom >= containerRect.top) {
          setIsHeaderBelowFaqsTop(true);
        } else {
          setIsHeaderBelowFaqsTop(false);
        }
        const isHeaderBottomWithinLinks =
          Math.abs(headerWrapperRect.bottom - containerRect.bottom) <=
          Math.abs(linksWrapperDesktopRect.height);

        if (isHeaderBottomWithinLinks) {
          setIsHeaderBottomWithinLinks(isHeaderBottomWithinLinks);
        } else {
          setIsHeaderBottomWithinLinks(false);
        }
      }
    }, 30),
    [],
  );
  useEffect(() => {
    window.addEventListener('scroll', handleScrollOrResize);
    window.addEventListener('resize', handleScrollOrResize);

    handleScrollOrResize();

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [handleScrollOrResize]);

  const scrollToElement = (id: string) => {
    const element = document.getElementById(id);
    const headerWrapper = document.querySelector(
      '[data-testid="HeaderWrapper"]',
    );
    const headerMegamenu = document.querySelector(
      '[data-testid="MegamenuComponent"]',
    );
    const isHeaderMegamenuPresent = Boolean(headerMegamenu);
    if (element && headerWrapper) {
      const headerHeight = headerWrapper.getBoundingClientRect().height;
      // `47` represents the height of the header MegamenuComponent
      const scrollDistanceDesktop: number = isHeaderMegamenuPresent ? 0 : -47;
      const scrollDistanceToAdd: number = isMdDown ? 0 : scrollDistanceDesktop;
      window.scrollTo({
        top:
          element.getBoundingClientRect().top +
          window.scrollY +
          scrollDistanceToAdd -
          headerHeight,
        behavior: 'smooth',
      });
    }
  };
  const handleScroll = (id: string, event: React.MouseEvent) => {
    event.preventDefault();
    const formattedId = id?.toLowerCase().replace(/\s+/g, '-');
    scrollToElement(formattedId);
  };
  const faqs = props?.faqWrapsCollection?.items;
  const headingTextList = props?.faqWrapsCollection?.items.map(
    (item: faqWrapsCollectionItemType) => item?.placeholderHeading?.text,
  );
  if (
    faqs &&
    faqs?.length > 0 &&
    headingTextList &&
    headingTextList?.length > 0
  ) {
    return (
      <S.StyledContainer ref={containerRef} maxWidth='lg'>
        <S.LinksWrapperMobile>
          <S.CategoriesTitle>{props?.categoryLabel}</S.CategoriesTitle>
          <S.StyledAccordion
            $isOpen={isOpen}
            isOpen={isOpen}
            onClick={() => setIsOpen(!isOpen)}
            title={
              <Typography
                fontFamily='italianPlate'
                fontWeight='bold'
                scale='p1'
                textTransform='uppercase'
              >
                {formatMessage(messages.allTheQuestions)}
              </Typography>
            }
          >
            {headingTextList.map((text) => {
              return (
                <S.LinkButtonWrapper key={text}>
                  <S.LinkButton
                    onClick={(e) => handleScroll(text as string, e)}
                    variant='link'
                  >
                    {text}
                  </S.LinkButton>
                </S.LinkButtonWrapper>
              );
            })}
          </S.StyledAccordion>
        </S.LinksWrapperMobile>

        <S.StyledGrid>
          <S.LinksWrapperContainer
            $isHeaderBottomWithinLinks={isHeaderBottomWithinLinks}
          >
            <S.LinksWrapperDesktop
              ref={desktopWrapperRef}
              $isHeaderBelowFaqsTop={isHeaderBelowFaqsTop}
              $isHeaderBottomWithinLinks={isHeaderBottomWithinLinks}
              $linksWrapperDesktopTop={headerBoxHeight}
              data-testid='linksWrapperDesktop'
            >
              <>
                <S.CategoriesTitleDesktop>
                  {props?.categoryLabel}
                </S.CategoriesTitleDesktop>
                {headingTextList.length > 0 && (
                  <S.LinkButtonWrapper>
                    <S.LinkButton
                      $hasUnderline={true}
                      $isAllTheQuestions={true}
                      onClick={(e) =>
                        handleScroll(headingTextList[0] as string, e)
                      }
                      variant='link'
                    >
                      {formatMessage(messages.allTheQuestions)}
                    </S.LinkButton>
                  </S.LinkButtonWrapper>
                )}
                {headingTextList.map((text) => {
                  return (
                    <S.LinkButtonWrapper key={text}>
                      <S.LinkButton
                        data-testid='handleScroll'
                        onClick={(e) => handleScroll(text as string, e)}
                        variant='link'
                      >
                        {text}
                      </S.LinkButton>
                    </S.LinkButtonWrapper>
                  );
                })}
              </>
            </S.LinksWrapperDesktop>
          </S.LinksWrapperContainer>
          <S.FaqWrapListWrapper>
            {faqs?.map((FaqsProps) => {
              return (
                <FaqWrap
                  isFromFaqWrapCategories={true}
                  {...(FaqsProps as ContentfulFaqWrapFragment)}
                  key={FaqsProps?.sys?.id}
                />
              );
            })}
          </S.FaqWrapListWrapper>
        </S.StyledGrid>
      </S.StyledContainer>
    );
  } else {
    return null;
  }
};

export const FaqCategories = (props: ContentfulFaqCategoriesFragment) => {
  if (props) {
    return RenderContentfulFaqCategoriesSection(props);
  } else {
    return null;
  }
};
